import { GridColDef } from '@mui/x-data-grid';
import { Link as RouterLink } from 'react-router-dom';
import {
  ICandidateFilter,
  IJobSearchFilterDTO,
  IPostgreJobsAnalyticsPayload,
  JobAssignmentStatusFilter,
} from 'wisestep-api';
import { WsColors } from 'wisestep-base';

import getEncodedQueryParams from '../../../../../utils/getEncodedQueryParams';
import { JobsWidget } from '../../../DerivedCharts/helper';
import { getDateRangeByTimePeriod } from '../../../utils/timeInfo';
import { overrideAnalyticsFilter, renderCellFor, toMinutes } from '../../helper';
import { renderSubmissionCountCell } from '../../submissions';
import { IPostgreJobsDTO } from '../types';

export function getEncodedJobFilters(analyticsFilter, query) {
  return getEncodedQueryParams({
    ...mapAnalyticsToJobFilter(analyticsFilter, query),
    query: '',
    page: 0,
    size: 20,
    sort: [],
    workspaceId: '',
  });
}

export const renderJobCountCell = (
  params,
  filterNameValuePairs: [string, string][],
  analyticsFilter: any[],
  query: IPostgreJobsAnalyticsPayload
) => {
  const overridenFilters = overrideAnalyticsFilter(analyticsFilter, filterNameValuePairs);

  return (
    <RouterLink
      to={`/jobs/?d=${getEncodedJobFilters(overridenFilters, query)}`}
      target="_blank"
      style={{
        textDecoration: 'none',
        color: WsColors.TheBlues700,
      }}
    >
      {params.value}
    </RouterLink>
  );
};

export const getJobColumns = (
  widgetName: JobsWidget,
  widgetData: IPostgreJobsDTO,
  query: IPostgreJobsAnalyticsPayload,
  analyticsFilter: ICandidateFilter[]
): GridColDef[] => {
  switch (widgetName) {
    case JobsWidget.TABLE_ACCOUNT_MANAGER:
      return [
        {
          field: 'accountManager',
          headerName: `Account Manager Name`,
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('ACCOUNT_MANAGER', params),
        },
        {
          field: 'count',
          headerName: `Total Count (${widgetData.totalCount})`,
          minWidth: 130,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [['ACCOUNT_MANAGERS', params.row.accountManagerEmail]],
              analyticsFilter,
              query
            ),
        },
      ];
    case JobsWidget.TABLE_CLIENT:
      return [
        {
          field: 'clientName',
          headerName: `Client Name`,
          minWidth: 120,
          flex: 2,
          renderCell: (params) => renderCellFor('CLIENT', params),
        },
        {
          field: 'count',
          headerName: `Jobs Count (${widgetData.totalCount})`,
          minWidth: 130,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [['CLIENT_NAMES', params.row.clientName]],
              analyticsFilter,
              query
            ),
        },
      ];
    case JobsWidget.TABLE_CLIENT_SPOC_STATUS_WISE:
      return [
        {
          field: 'clientContactName',
          headerName: 'Client Spoc Name',
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('CLIENT_RECRUITER', params),
        },
        {
          field: 'count',
          headerName: `Total (${widgetData.totalCount})`,
          minWidth: 130,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [['CLIENT_RECRUITERS', params.row.clientContactEmail]],
              analyticsFilter,
              query
            ),
        },
        {
          field: 'open',
          headerName: `Open (${widgetData.totalOpen})`,
          minWidth: 110,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [
                ['CLIENT_RECRUITERS', params.row.clientContactEmail],
                ['JOB_STATUS', 'Opened'],
              ],
              analyticsFilter,
              query
            ),
        },
        {
          field: 'closed',
          headerName: `Closed (${widgetData.totalClosed})`,
          minWidth: 110,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [
                ['CLIENT_RECRUITERS', params.row.clientContactEmail],
                ['JOB_STATUS', 'Closed'],
              ],
              analyticsFilter,
              query
            ),
        },
      ];
    case JobsWidget.TABLE_CLIENT_SPOC_TYPE_WISE:
      return [
        {
          field: 'clientContactName',
          headerName: 'Client Spoc Name',
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('CLIENT_RECRUITER', params),
        },
        {
          field: 'count',
          headerName: `Total (${widgetData.totalCount})`,
          minWidth: 130,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [['CLIENT_RECRUITERS', params.row.clientContactEmail]],
              analyticsFilter,
              query
            ),
        },
        {
          field: 'permanent',
          headerName: `Permanent (${widgetData.totalPermanent})`,
          minWidth: 120,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [
                ['CLIENT_RECRUITERS', params.row.clientContactEmail],
                ['JOB_TYPE', 'Permanent'],
              ],
              analyticsFilter,
              query
            ),
        },
        {
          field: 'contract',
          headerName: `Contract (${widgetData.totalContract})`,
          minWidth: 120,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [
                ['CLIENT_RECRUITERS', params.row.clientContactEmail],
                ['JOB_TYPE', 'Contract'],
              ],
              analyticsFilter,
              query
            ),
        },
        {
          field: 'temporary',
          headerName: `Temporary (${widgetData.totalTemporary})`,
          minWidth: 120,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [
                ['CLIENT_RECRUITERS', params.row.clientContactEmail],
                ['JOB_TYPE', 'Temporary'],
              ],
              analyticsFilter,
              query
            ),
        },
      ];
    case JobsWidget.TABLE_CLIENT_STATUS_WISE:
      return [
        {
          field: 'clientName',
          headerName: 'Client Name',
          minWidth: 120,
          flex: 2,
          renderCell: (params) => renderCellFor('CLIENT', params),
        },
        {
          field: 'count',
          headerName: `Total (${widgetData.totalCount})`,
          minWidth: 130,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [['CLIENT_NAMES', params.row.clientName]],
              analyticsFilter,
              query
            ),
        },
        {
          field: 'open',
          headerName: `Open (${widgetData.totalOpen})`,
          minWidth: 110,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [
                ['CLIENT_NAMES', params.row.clientName],
                ['JOB_STATUS', 'Opened'],
              ],
              analyticsFilter,
              query
            ),
        },
        {
          field: 'closed',
          headerName: `Closed (${widgetData.totalClosed})`,
          minWidth: 110,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [
                ['CLIENT_NAMES', params.row.clientName],
                ['JOB_STATUS', 'Closed'],
              ],
              analyticsFilter,
              query
            ),
        },
      ];
    case JobsWidget.TABLE_CLIENT_TYPE_WISE:
      return [
        {
          field: 'clientName',
          headerName: 'Client Name',
          minWidth: 120,
          flex: 2,
          renderCell: (params) => renderCellFor('CLIENT', params),
        },
        {
          field: 'count',
          headerName: `Total (${widgetData.totalCount})`,
          minWidth: 130,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [['CLIENT_NAMES', params.row.clientName]],
              analyticsFilter,
              query
            ),
        },
        {
          field: 'permanent',
          headerName: `Permanent (${widgetData.totalPermanent})`,
          minWidth: 130,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [
                ['CLIENT_NAMES', params.row.clientName],
                ['JOB_TYPE', 'Permanent'],
              ],
              analyticsFilter,
              query
            ),
        },
        {
          field: 'contract',
          headerName: `Contract (${widgetData.totalContract})`,
          minWidth: 130,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [
                ['CLIENT_NAMES', params.row.clientName],
                ['JOB_TYPE', 'Contract'],
              ],
              analyticsFilter,
              query
            ),
        },
        {
          field: 'temporary',
          headerName: `Temporary (${widgetData.totalTemporary})`,
          minWidth: 130,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [
                ['CLIENT_NAMES', params.row.clientName],
                ['JOB_TYPE', 'Temporary'],
              ],
              analyticsFilter,
              query
            ),
        },
      ];
    case JobsWidget.TABLE_CLIENT_SPOC:
      return [
        {
          field: 'clientContactName',
          headerName: `Client Spoc Name`,
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('CLIENT_RECRUITER', params),
        },
        {
          field: 'count',
          headerName: `Total Count (${widgetData.totalCount})`,
          minWidth: 130,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(
              params,
              [['CLIENT_RECRUITERS', params.row.clientContactEmail]],
              analyticsFilter,
              query
            ),
        },
      ];
    case JobsWidget.TABLE_ACCEPTED_NO_SUBMISSION:
    case JobsWidget.TABLE_ACCEPTED_NO_MAPPING:
      return [
        {
          field: 'jobTitle',
          headerName: 'Job Title',
          minWidth: 160,
          flex: 1.5,
        },
        {
          field: 'jobCode',
          headerName: 'Job Code',
          width: 100,
          renderCell: (params) => renderCellFor('JOB', params),
        },
        {
          field: 'clientName',
          headerName: 'Client Name',
          minWidth: 130,
          flex: 1,
          renderCell: (params) => renderCellFor('CLIENT', params),
        },
        {
          field: 'clientRecruiter',
          headerName: 'Client Recruiter',
          minWidth: 160,
          flex: 1,
          renderCell: (params) => renderCellFor('CLIENT_RECRUITER', params),
        },
        {
          field: 'accountManager',
          headerName: 'Account Manager',
          minWidth: 160,
          flex: 1,
          renderCell: (params) => renderCellFor('ACCOUNT_MANAGER', params),
        },
        { field: 'jobStatus', headerName: 'Job Status', width: 100 },
      ];
    case JobsWidget.TABLE_SUBMISSION_COUNT:
      return [
        {
          field: 'submissionCount',
          headerName: `Submissions (${widgetData.resultSet?.reduce(
            (acc, jobSubmissionCount) =>
              acc + jobSubmissionCount['submissionCount'] * jobSubmissionCount['jobCount'],
            0
          )})`,
          flex: 1,
          renderCell: (params) =>
            renderSubmissionCountCell(
              params,
              [['JOB_IDS', params.row.jobIds]],
              analyticsFilter,
              query
            ),
        },
        {
          field: 'jobCount',
          headerName: `Jobs (${widgetData.totalCount})`,
          flex: 1,
          renderCell: (params) =>
            renderJobCountCell(params, [['JOB_IDS', params.row.jobIds]], analyticsFilter, query),
        },
      ];
    case JobsWidget.TABLE_JOB_TIME_TO_SERVICE:
      return [
        {
          field: 'job',
          headerName: 'Job',
          minWidth: 140,
          flex: 1.5,
          renderCell: (params) => renderCellFor('JOB', params),
        },
        {
          field: 'clientName',
          headerName: 'Client Name',
          minWidth: 140,
          flex: 1.5,
        },
        {
          field: 'accountManagerName',
          headerName: 'Account Manager Name',
          minWidth: 160,
          flex: 1.5,
        },
        {
          field: 'timeTakenForFirstAssignment',
          headerName: 'Time Taken for First Assignment',
          minWidth: 100,
          flex: 1,
          sortComparator: (v1, v2) => {
            return toMinutes(v1) - toMinutes(v2);
          },
        },
        {
          field: 'timeTakenForFirstAcceptance',
          headerName: 'Time Taken for First Acceptance',
          minWidth: 100,
          flex: 1,
          sortComparator: (v1, v2) => {
            return toMinutes(v1) - toMinutes(v2);
          },
        },
        {
          field: 'timeForFirstMapping',
          headerName: 'Time for First mapping',
          minWidth: 100,
          flex: 1,
          sortComparator: (v1, v2) => {
            return toMinutes(v1) - toMinutes(v2);
          },
        },
        {
          field: 'minTimeForSubmission',
          headerName: 'Min Time for Submission',
          minWidth: 100,
          flex: 1,
          sortComparator: (v1, v2) => {
            return toMinutes(v1) - toMinutes(v2);
          },
        },
        {
          field: 'medianTimeForSubmission',
          headerName: 'Median Time for Submission',
          minWidth: 100,
          flex: 1,
          sortComparator: (v1, v2) => {
            return toMinutes(v1) - toMinutes(v2);
          },
        },
        {
          field: 'maxTimeForSubmission',
          headerName: 'Max Time for Submission',
          minWidth: 100,
          flex: 1,
          sortComparator: (v1, v2) => {
            return toMinutes(v1) - toMinutes(v2);
          },
        },
      ];
    default:
      throw new Error('Invalid Table Column Data');
  }
};

function mapAnalyticsToJobFilter(
  analyticsFilters: ICandidateFilter[],
  query: IPostgreJobsAnalyticsPayload
): IJobSearchFilterDTO {
  const jobSearchFilter = {} as IJobSearchFilterDTO & {
    clientIds: string[];
    clientContactIds: string[];
  };
  jobSearchFilter.searchAcross = [];

  overrideQueryFilter(query.filters ?? [], analyticsFilters).forEach((analyticsFilter) => {
    const filterValues = (analyticsFilter.values ?? []) as string[];
    if (filterValues.length == 0) return;

    switch (analyticsFilter.filterName.toUpperCase()) {
      case 'CLIENT_NAMES':
        jobSearchFilter.clientNames = filterValues;
        break;
      case 'CLIENT_RECRUITERS':
        jobSearchFilter.clientRecruiterEmails = filterValues;
        break;
      case 'DATE_RANGE': {
        const values = JSON.parse(filterValues[0] as string);
        values.fromDate &&
          (jobSearchFilter.postedFromDate = new Date(values.fromDate).toISOString());
        values.toDate && (jobSearchFilter.postedToDate = new Date(values.toDate).toISOString());
        break;
      }
      case 'RECRUITER_NAMES':
        jobSearchFilter.postedByRecruiterEmails = filterValues;
        break;
      case 'ACCOUNT_MANAGERS':
        jobSearchFilter.postedByAccountManagerEmails = filterValues;
        break;
      case 'TEAM_IDS':
        jobSearchFilter.searchAcrossTeams = filterValues;
        break;
      case 'JOB_STATUS': {
        const jobStatus = filterValues[0].toLowerCase();
        jobSearchFilter.jobStatus = jobStatus.includes('open')
          ? 'Open'
          : jobStatus.includes('close')
          ? 'Closed'
          : 'All';
        break;
      }
      case 'JOB_TYPE':
        jobSearchFilter.jobType = filterValues[0];
        break;
      case 'JOB_IDS':
        jobSearchFilter.jobIds = filterValues;
        break;
      case 'JOB_ASSIGNMENT_STATUS':
        jobSearchFilter.jobAssignmentStatusFilter = filterValues[0] as JobAssignmentStatusFilter;
        break;
      case 'SUBMISSIONS_COUNT':
        jobSearchFilter.submissionsCountRange = JSON.parse(filterValues[0]);
        break;
      case 'BUSINESS_UNIT_ID':
        jobSearchFilter.businessUnit = filterValues.toString();
        break;
      case 'CLIENT_ID':
        jobSearchFilter.clientIds = filterValues;
        break;
      case 'CLIENT_CONTACT_ID':
        jobSearchFilter.clientContactIds = filterValues;
    }
  });

  if (!jobSearchFilter.postedFromDate && !jobSearchFilter.postedToDate) {
    const dateTime = getDateRangeByTimePeriod(query.range as string);
    jobSearchFilter.postedFromDate = new Date(dateTime.fromDateTime).toISOString();
    jobSearchFilter.postedToDate = new Date(dateTime.toDateTime).toISOString();
  }
  if (!jobSearchFilter.jobStatus) jobSearchFilter.jobStatus = 'All';
  if (!jobSearchFilter.jobAssignmentStatusFilter)
    jobSearchFilter.jobAssignmentStatusFilter = JobAssignmentStatusFilter.Posted;

  switch (query.scope?.toUpperCase()) {
    case 'WORKSPACE':
      jobSearchFilter.searchAcross.push('ACROSS_WORKSPACE');
      break;
    case 'TEAMS':
      jobSearchFilter.searchAcross.push('MY_TEAMS');
      break;
    case 'LOGGEDINUSER':
      jobSearchFilter.searchAcross.push('MY_PERSONAL_DATA');
  }
  return jobSearchFilter;
}

export function overrideQueryFilter(
  queryFilters: ICandidateFilter[],
  analyticsGlobalFilters: ICandidateFilter[]
): ICandidateFilter[] {
  const filterNameObjectMap: Record<string, ICandidateFilter> = {};
  queryFilters.forEach((filter) => {
    filterNameObjectMap[filter.filterName] = filter;
  });
  analyticsGlobalFilters.forEach((filter) => {
    filterNameObjectMap[filter.filterName] = filter;
  });
  return Object.values(filterNameObjectMap);
}

export const defaultJobsWidgetData: IPostgreJobsDTO = {
  count: 0,
  totalCount: 0,
  totalClosed: 0,
  totalContract: 0,
  totalOpen: 0,
  totalPermanent: 0,
  totalTemporary: 0,
  resultSet: [],
  series: [],
  categories: [],
  response: [],
};
